import React, { Component, Suspense, lazy } from "react";
import { Navigate, Route } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";

import ProtectedRoute from "../utils/ProtectedRoute.jsx";
import DashboardLayout from "../components/hotel/DashboardLayout.jsx";
import { Button } from "../components/ui/button.jsx";
import { HOTEL_PERMISSIONS as P } from "../constants/hotelPermissions";

const RouteFallback = () => (
  <div className="flex min-h-[50vh] items-center justify-center px-4 text-sm text-muted-foreground">
    Loading page...
  </div>
);

class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    if (import.meta.env.DEV) {
      console.error("Hotel route failed to render.", error, info);
    }

    const isChunkOrMimeError =
      error?.name === "TypeError" &&
      (error?.message?.includes("text/html") ||
        error?.message?.includes("dynamically imported module") ||
        error?.message?.includes("MIME type"));

    if (isChunkOrMimeError && typeof window !== "undefined") {
      const reloaded = sessionStorage.getItem("page_chunk_reload_attempt");
      if (!reloaded) {
        sessionStorage.setItem("page_chunk_reload_attempt", "true");
        window.location.reload();
      }
    }
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4">
        <div className="w-full max-w-lg rounded-2xl border bg-card p-6 text-center shadow-sm">
          <h2 className="text-xl font-semibold text-foreground">
            This page could not load
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Refresh the page and try again. If it keeps happening, share the
            browser console error with the team.
          </p>
          {import.meta.env.DEV && this.state.error?.message ? (
            <p className="mt-4 rounded-xl bg-muted px-3 py-2 text-left text-xs text-muted-foreground">
              {this.state.error.message}
            </p>
          ) : null}
          <Button
            type="button"
            variant="primary"
            className="mt-5"
            onClick={() => window.location.reload()}
          >
            Reload page
          </Button>
        </div>
      </div>
    );
  }
}

const lazyWithRetry = (loader) => {
  return lazy(async () => {
    try {
      const component = await loader();
      if (typeof window !== "undefined") {
        sessionStorage.removeItem("page_chunk_reload_attempt");
      }
      return component;
    } catch (error) {
      const isChunkOrMimeError =
        error?.name === "TypeError" &&
        (error?.message?.includes("text/html") ||
          error?.message?.includes("dynamically imported module") ||
          error?.message?.includes("MIME type"));

      if (isChunkOrMimeError && typeof window !== "undefined") {
        const reloaded = sessionStorage.getItem("page_chunk_reload_attempt");
        if (!reloaded) {
          sessionStorage.setItem("page_chunk_reload_attempt", "true");
          window.location.reload();
          return new Promise(() => {});
        }
      }
      throw error;
    }
  });
};

const lazyPage = (loader) => {
  const Component = lazyWithRetry(loader);

  return function LazyRoutePage(props) {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Component {...props} />
        </Suspense>
      </RouteErrorBoundary>
    );
  };
};

const DashboardPage = lazyPage(() => import("../pages/hotel/DashboardPage.jsx"));
const PmsCommandCenterPage = lazyPage(() => import("../pages/hotel/PmsCommandCenterPage.jsx"));
const ActiveRequestsPage = lazyPage(() => import("../pages/hotel/GuestRequest/ActiveRequestsPage.jsx"));
const GuestRequestDetailsPage = lazyPage(() => import("../pages/hotel/GuestRequest/GuestRequestDetailsPage.jsx"));
const RoomsPage = lazyPage(() => import("../pages/hotel/Rooms/RoomPage.jsx"));
const RoomTypesPage = lazyPage(() => import("../pages/hotel/Rooms/RoomTypesPage.jsx"));
const GuestSessionsPage = lazyPage(() => import("../pages/hotel/GuestSession/GuestSessionPage.jsx"));
const QRGeneratorPage = lazyPage(() => import("../pages/hotel/GuestCheckin/QRGeneratorPage.jsx"));
const SettingsPage = lazyPage(() => import("../pages/hotel/SettingsPage.jsx"));
const WebContactLinksPage = lazyPage(() => import("../pages/hotel/Settings/WebContactLinksPage.jsx"));
const HotelServicesPage = lazyPage(() => import("../pages/hotel/HotelServicePage.jsx"));
const PartnerServicesPage = lazyPage(() => import("../pages/hotel/PartnerServices/PartnerServicesPage.jsx"));
const HotelSurveyBuilderPage = lazyPage(() => import("../pages/hotel/HotelSurveyBuilderPage.jsx"));
const HotelSurveyResponsesPage = lazyPage(() => import("../pages/hotel/HotelSurveyResponsesPage.jsx"));
const LoginPage = lazyPage(() => import("../pages/hotel/Auth/LoginPage.jsx"));
const RegisterPage = lazyPage(() => import("../pages/hotel/Auth/RegisterPage.jsx"));
const OrganizationSetupPage = lazyPage(() => import("../pages/hotel/organization/OrganizationSetupPage.jsx"));
const RoomDetailsPage = lazyPage(() => import("../pages/hotel/Rooms/RoomsDetailsPage.jsx"));
const HousekeepingBoardPage = lazyPage(() => import("../pages/hotel/Housekeeping/HousekeepingBoardPage.jsx"));
const NightAuditPage = lazyPage(() => import("../pages/hotel/NightAudit/NightAuditPage.jsx"));
const OrderViewPage = lazyPage(() => import("../pages/hotel/pos/OrderViewPage.jsx"));
const OrdersPage = lazyPage(() => import("../pages/hotel/pos/OrderPage.jsx"));
const ExtrasPage = lazyPage(() => import("../pages/hotel/pos/ExtrasPage.jsx"));
const ExtrasFormPage = lazyPage(() => import("../pages/hotel/pos/ExtrasFormPage.jsx"));
const MenuManagementPage = lazyPage(() => import("../pages/hotel/pos/MenuManagementPage.jsx"));
const AdminUsersPage = lazyPage(() => import("../pages/hotel/admin/AdminUsersPage.jsx"));
const AdminRolesPage = lazyPage(() => import("../pages/hotel/admin/AdminRolesPage.jsx"));
const DepartmentManagementPage = lazyPage(() => import("../pages/hotel/admin/DepartmentManagementPage.jsx"));
const RecommendedActivitiesPage = lazyPage(() => import("../pages/hotel/RecommendedActivities/RecommendedActivitiesPage.jsx"));
const BillingPage = lazyPage(() => import("../pages/hotel/BillingPage.jsx"));
const BillingDashboardPage = lazyPage(() => import("../pages/hotel/BillingDashboardPage.jsx"));
const BillDetailPage = lazyPage(() => import("../pages/hotel/BillDetailPage.jsx"));
const BookingManagementPage = lazyPage(() => import("../pages/hotel/Bookings/BookingManagementPage.jsx"));
const BookingFormPage = lazyPage(() => import("../pages/hotel/Bookings/BookingFormPage.jsx"));
const BookingDetailPage = lazyPage(() => import("../pages/hotel/Bookings/BookingDetailPage.jsx"));
const CheckAvailabilityPage = lazyPage(() => import("../pages/hotel/Bookings/CheckAvailabilityPage.jsx"));
const BookingReportsPage = lazyPage(() => import("../pages/hotel/Bookings/BookingReportsPage.jsx"));
const GuestsPage = lazyPage(() => import("../pages/hotel/Guests/GuestsPage.jsx"));
const GuestDetailPage = lazyPage(() => import("../pages/hotel/Guests/GuestDetailPage.jsx"));
const GuestReportsPage = lazyPage(() => import("../pages/hotel/Guests/GuestReportsPage.jsx"));
const KnowYourGuestPage = lazyPage(() => import("../pages/hotel/KnowYourGuest/KnowYourGuestPage.jsx"));
const BookersPage = lazyPage(() => import("../pages/hotel/Bookers/BookersPage.jsx"));
const BookerDetailPage = lazyPage(() => import("../pages/hotel/Bookers/BookerDetailPage.jsx"));
const BookerRewardsPage = lazyPage(() => import("../pages/hotel/Bookers/BookerRewardsPage.jsx"));
const BookerReportsPage = lazyPage(() => import("../pages/hotel/Bookers/BookerReportsPage.jsx"));
const LoyaltyProgramPage = lazyPage(() => import("../pages/hotel/Loyalty/LoyaltyProgramPage.jsx"));
const PartnerRedemptionsPage = lazyPage(() => import("../pages/hotel/Loyalty/PartnerRedemptionsPage.jsx"));
const MembershipsPage = lazyPage(() => import("../pages/hotel/Memberships/MembershipsPage.jsx"));
const MembershipEventsPage = lazyPage(() => import("../pages/hotel/Memberships/MembershipEventsPage.jsx"));
const CouponsPage = lazyPage(() => import("../pages/hotel/Memberships/CouponsPage.jsx"));
const CouponRedeemPage = lazyPage(() => import("../pages/hotel/Memberships/CouponRedeemPage.jsx"));
const CreateOrderPage = lazyPage(() => import("../pages/hotel/pos/CreateOrderPage.jsx"));
const PaymentMethodsPage = lazyPage(() => import("../pages/hotel/pos/PaymentMethodsPage.jsx"));
const FinanceSettingsPage = lazyPage(() => import("../pages/hotel/FinanceSettingsPage.jsx"));
const BulkPriceManagerPage = lazyPage(() => import("../pages/hotel/BulkPriceManagerPage.jsx"));
const GuestSessionHistoryPage = lazyPage(() => import("../pages/hotel/GuestSession/GuestSessionHistoryPage.jsx"));
const RoomAccessQrManagementPage = lazyPage(() => import("../pages/hotel/GuestCheckin/RoomAccessQrManagementPage.jsx"));
const DepartmentAlertCenterPage = lazyPage(() => import("../pages/hotel/Calls/DepartmentAlertCenterPage.jsx"));
const IncomingCallPage = lazyPage(() => import("../pages/hotel/Calls/IncomingCallPage.jsx"));
const CallDirectoryPage = lazyPage(() => import("../pages/hotel/Calls/CallDirectoryPage.jsx"));
const ClientsPage = lazyPage(() => import("../pages/hotel/pos/ClientsPage.jsx"));
const ClientFormPage = lazyPage(() => import("../pages/hotel/pos/ClientFormPage.jsx"));
const PosDashboardPage = lazyPage(() => import("../pages/hotel/pos/PosDashboardPage.jsx"));

const PermissionRoute = ({ permission, permissions = [], children }) => {
  const requiredPermissions = Array.isArray(permission)
    ? permission
    : permission
      ? [permission]
      : permissions;

  if (requiredPermissions.length === 0) {
    return children;
  }

  return (
    <ProtectedRoute requiredPermissions={requiredPermissions}>{children}</ProtectedRoute>
  );
};

const RequireCapabilityRoute = ({ capability, children }) => {
  const { organizationCapabilities } = useAuth();

  if (capability && !organizationCapabilities?.[capability]) {
    return <Navigate to="/hotel/dashboard" replace />;
  }

  return children;
};

const HideCapabilityRoute = ({ hideCapability, children }) => {
  const { organizationCapabilities } = useAuth();

  if (hideCapability && organizationCapabilities?.[hideCapability]) {
    return <Navigate to="/hotel/dashboard" replace />;
  }

  return children;
};

const HotelRoutes = () => {
  return (
    <>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      <Route
        path="/hotel/setup"
        element={
          <ProtectedRoute>
            <OrganizationSetupPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/hotel"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/hotel/dashboard" replace />} />

        <Route
          path="dashboard"
          element={
            <PermissionRoute permission={P.dashboard.view}>
              <DashboardPage />
            </PermissionRoute>
          }
        />
        <Route
          path="pms-command-center"
          element={
            <PermissionRoute permission={P.dashboard.view}>
              <PmsCommandCenterPage />
            </PermissionRoute>
          }
        />
        <Route
          path="calls"
          element={
            <RequireCapabilityRoute capability="calls">
              <PermissionRoute permission={P.calls.view}>
                <DepartmentAlertCenterPage />
              </PermissionRoute>
            </RequireCapabilityRoute>
          }
        />
        <Route
          path="calls/directory"
          element={
            <RequireCapabilityRoute capability="calls">
              <PermissionRoute permission={P.calls.view}>
                <CallDirectoryPage />
              </PermissionRoute>
            </RequireCapabilityRoute>
          }
        />
        <Route
          path="calls/incoming/:callId"
          element={
            <PermissionRoute permission={P.calls.receive}>
              <IncomingCallPage />
            </PermissionRoute>
          }
        />
        <Route
          path="calls/:alertId"
          element={
            <PermissionRoute permission={P.calls.view}>
              <DepartmentAlertCenterPage />
            </PermissionRoute>
          }
        />
        <Route
          path="requests"
          element={
            <RequireCapabilityRoute capability="guestPortal">
              <PermissionRoute permission={P.requests.view}>
                <ActiveRequestsPage />
              </PermissionRoute>
            </RequireCapabilityRoute>
          }
        />
        <Route
          path="requests/:id"
          element={
            <PermissionRoute permission={P.requests.view}>
              <GuestRequestDetailsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="service-requests"
          element={
            <RequireCapabilityRoute capability="guestPortal">
              <PermissionRoute permission={P.requests.view}>
                <ActiveRequestsPage />
              </PermissionRoute>
            </RequireCapabilityRoute>
          }
        />
        <Route
          path="service-requests/:id"
          element={
            <PermissionRoute permission={P.requests.view}>
              <GuestRequestDetailsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="rooms"
          element={
            <RequireCapabilityRoute capability="hotel">
              <PermissionRoute permission={P.rooms.view}>
                <RoomsPage />
              </PermissionRoute>
            </RequireCapabilityRoute>
          }
        />
        <Route
          path="housekeeping"
          element={
            <RequireCapabilityRoute capability="hotel">
              <PermissionRoute permission={[P.housekeeping?.view, P.rooms.view].filter(Boolean)}>
                <HousekeepingBoardPage />
              </PermissionRoute>
            </RequireCapabilityRoute>
          }
        />
        <Route
          path="room-maintenance"
          element={<Navigate to="/hotel/housekeeping?tab=maintenance" replace />}
        />
        <Route
          path="room-types"
          element={
            <PermissionRoute permission={P.roomTypes.view}>
              <RoomTypesPage />
            </PermissionRoute>
          }
        />
        <Route
          path="sessions"
          element={
            <RequireCapabilityRoute capability="hotel">
              <PermissionRoute permission={P.sessions.view}>
                <GuestSessionsPage />
              </PermissionRoute>
            </RequireCapabilityRoute>
          }
        />
        <Route
          path="sessions/:sessionId/history"
          element={
            <RequireCapabilityRoute capability="hotel">
              <PermissionRoute permission={P.sessions.view}>
                <GuestSessionHistoryPage />
              </PermissionRoute>
            </RequireCapabilityRoute>
          }
        />
        <Route
          path="guests"
          element={
            <PermissionRoute permission={P.guestCrm.view}>
              <GuestsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="guests/reports"
          element={
            <PermissionRoute permission={P.guestCrm.reportsView}>
              <GuestReportsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="guests/:id"
          element={
            <PermissionRoute permission={P.guestCrm.view}>
              <GuestDetailPage />
            </PermissionRoute>
          }
        />
        <Route
          path="know-your-guest"
          element={
            <PermissionRoute permission={P.knowYourGuest.view}>
              <KnowYourGuestPage />
            </PermissionRoute>
          }
        />
        <Route
          path="bookers"
          element={
            <PermissionRoute permission={P.bookers.view}>
              <BookersPage />
            </PermissionRoute>
          }
        />
        <Route
          path="bookers/reports"
          element={
            <PermissionRoute permission={P.bookers.reportsView}>
              <BookerReportsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="bookers/:bookerId"
          element={
            <PermissionRoute permission={P.bookers.view}>
              <BookerDetailPage />
            </PermissionRoute>
          }
        />
        <Route
          path="booker-rewards"
          element={
            <PermissionRoute permission={P.bookers.view}>
              <BookerRewardsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="loyalty"
          element={
            <PermissionRoute permission={P.guestCrm.view}>
              <LoyaltyProgramPage />
            </PermissionRoute>
          }
        />
        <Route
          path="memberships"
          element={
            <PermissionRoute permission={P.guestCrm.view}>
              <MembershipsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="membership-events"
          element={
            <PermissionRoute permission={P.guestCrm.view}>
              <MembershipEventsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="coupons"
          element={
            <PermissionRoute permission={P.guestCrm.view}>
              <CouponsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="coupons/redeem"
          element={
            <PermissionRoute permission={P.guestCrm.view}>
              <CouponRedeemPage />
            </PermissionRoute>
          }
        />
        <Route
          path="partner-redemptions"
          element={
            <PermissionRoute permission={P.guestCrm.view}>
              <PartnerRedemptionsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="bookings"
          element={
            <RequireCapabilityRoute capability="bookings">
              <PermissionRoute permission={P.bookings.view}>
                <BookingManagementPage />
              </PermissionRoute>
            </RequireCapabilityRoute>
          }
        />
        <Route
          path="bookings/reports"
          element={
            <PermissionRoute permission={P.bookings.reportsView}>
              <BookingReportsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="check-availability"
          element={
            <RequireCapabilityRoute capability="hotel">
              <PermissionRoute permission={P.availability.view}>
                <CheckAvailabilityPage />
              </PermissionRoute>
            </RequireCapabilityRoute>
          }
        />
        <Route
          path="bookings/create"
          element={
            <PermissionRoute permission={P.bookings.create}>
              <BookingFormPage />
            </PermissionRoute>
          }
        />
        <Route
          path="bookings/:id"
          element={
            <PermissionRoute permission={P.bookings.view}>
              <BookingDetailPage />
            </PermissionRoute>
          }
        />
        <Route
          path="bookings/:id/edit"
          element={
            <PermissionRoute permission={P.bookings.edit}>
              <BookingFormPage />
            </PermissionRoute>
          }
        />
        <Route
          path="billing"
          element={
            <RequireCapabilityRoute capability="guestBilling">
              <PermissionRoute permission={P.billing.view}>
                <BillingPage />
              </PermissionRoute>
            </RequireCapabilityRoute>
          }
        />
        <Route
          path="billing/dashboard"
          element={
            <PermissionRoute permission={P.billing.view}>
              <BillingDashboardPage />
            </PermissionRoute>
          }
        />
        <Route
          path="billing/:billId"
          element={
            <PermissionRoute permission={P.billing.view}>
              <BillDetailPage />
            </PermissionRoute>
          }
        />
        <Route
          path="night-audit"
          element={
            <PermissionRoute permission={P.nightAudit?.view || P.billing.view}>
              <NightAuditPage />
            </PermissionRoute>
          }
        />
        <Route
          path="finance/payment-methods"
          element={
            <PermissionRoute permission={P.settings.view}>
              <PaymentMethodsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="finance/settings"
          element={
            <PermissionRoute permission={P.settings.view}>
              <FinanceSettingsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="finance/bulk-pricing"
          element={
            <PermissionRoute permission={P.settings.view}>
              <BulkPriceManagerPage />
            </PermissionRoute>
          }
        />
        <Route
          path="qr-generator"
          element={
            <RequireCapabilityRoute capability="hotel">
              <PermissionRoute permission={P.qr.view}>
                <QRGeneratorPage />
              </PermissionRoute>
            </RequireCapabilityRoute>
          }
        />
        <Route
          path="settings"
          element={
            <PermissionRoute permission={P.settings.view}>
              <SettingsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="settings/contact-links"
          element={
            <PermissionRoute permission={P.settings.view}>
              <WebContactLinksPage />
            </PermissionRoute>
          }
        />
        <Route
          path="contact-links"
          element={
            <PermissionRoute permission={P.settings.view}>
              <WebContactLinksPage />
            </PermissionRoute>
          }
        />
        <Route
          path="services"
          element={
            <PermissionRoute permission={P.amenities.view}>
              <HotelServicesPage />
            </PermissionRoute>
          }
        />
        <Route
          path="partner-services"
          element={
            <PermissionRoute permission={P.amenities.view}>
              <PartnerServicesPage />
            </PermissionRoute>
          }
        />
        <Route
          path="surveys"
          element={
            <PermissionRoute permission={P.feedback.view}>
              <HotelSurveyBuilderPage />
            </PermissionRoute>
          }
        />
        <Route
          path="surveys/responses"
          element={
            <PermissionRoute permission={P.feedback.view}>
              <HotelSurveyResponsesPage />
            </PermissionRoute>
          }
        />
        <Route
          path="surveys/responses/:responseId"
          element={
            <PermissionRoute permission={P.feedback.view}>
              <HotelSurveyResponsesPage />
            </PermissionRoute>
          }
        />
        <Route
          path="rooms/:id"
          element={
            <PermissionRoute permission={P.rooms.view}>
              <RoomDetailsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="pos/orders"
          element={
            <PermissionRoute permission={P.pos.orders.view}>
              <OrdersPage />
            </PermissionRoute>
          }
        />
        <Route
          path="room-dining"
          element={<Navigate to="/hotel/pos/orders" replace />}
        />
        <Route
          path="departments"
          element={<Navigate to="/hotel/calls" replace />}
        />
        <Route
          path="pos/orders/create"
          element={
            <PermissionRoute permission={P.pos.orders.create}>
              <CreateOrderPage />
            </PermissionRoute>
          }
        />
        <Route
          path="pos/orders/:id"
          element={
            <PermissionRoute permission={P.pos.orders.view}>
              <OrderViewPage />
            </PermissionRoute>
          }
        />
        <Route
          path="pos/extras"
          element={
            <PermissionRoute permission={P.pos.menu.view}>
              <ExtrasPage />
            </PermissionRoute>
          }
        />

        <Route
          path="pos/extras/create"
          element={
            <PermissionRoute permission={P.pos.menu.create}>
              <ExtrasFormPage />
            </PermissionRoute>
          }
        />
        <Route
          path="pos/extras/:id"
          element={
            <PermissionRoute permission={P.pos.menu.update}>
              <ExtrasFormPage />
            </PermissionRoute>
          }
        />
        <Route
          path="pos/menu-management"
          element={
            <PermissionRoute permission={P.pos.menu.view}>
              <MenuManagementPage />
            </PermissionRoute>
          }
        />

        <Route
          path="pos/clients"
          element={
            <PermissionRoute permission={P.pos.clients.view}>
              <HideCapabilityRoute hideCapability="hotel">
                <ClientsPage />
              </HideCapabilityRoute>
            </PermissionRoute>
          }
        />
        <Route
          path="pos/clients/create"
          element={
            <PermissionRoute permission={P.pos.clients.create}>
              <HideCapabilityRoute hideCapability="hotel">
                <ClientFormPage />
              </HideCapabilityRoute>
            </PermissionRoute>
          }
        />
        <Route
          path="pos/clients/:id/edit"
          element={
            <PermissionRoute permission={P.pos.clients.update}>
              <HideCapabilityRoute hideCapability="hotel">
                <ClientFormPage />
              </HideCapabilityRoute>
            </PermissionRoute>
          }
        />
        <Route
          path="pos/dashboard"
          element={
            <RequireCapabilityRoute capability="pos">
              <PermissionRoute permission={P.pos.dashboard.view}>
                <PosDashboardPage />
              </PermissionRoute>
            </RequireCapabilityRoute>
          }
        />
        <Route
          path="recommended-activities"
          element={
            <PermissionRoute permission={P.recommendations.view}>
              <RecommendedActivitiesPage />
            </PermissionRoute>
          }
        />
        <Route
          path="admin/users"
          element={
            <PermissionRoute permission={P.users.view}>
              <AdminUsersPage />
            </PermissionRoute>
          }
        />
        <Route
          path="admin/staff"
          element={
            <PermissionRoute permission={P.users.view}>
              <Navigate to="/hotel/admin/users" replace />
            </PermissionRoute>
          }
        />
        <Route
          path="admin/departments"
          element={
            <PermissionRoute permission={P.departments.view}>
              <DepartmentManagementPage />
            </PermissionRoute>
          }
        />
        <Route
          path="admin/roles"
          element={
            <PermissionRoute permission={P.roles.view}>
              <AdminRolesPage />
            </PermissionRoute>
          }
        />
        <Route
          path="room-access-qr"
          element={
            <PermissionRoute permission={P.qr.view}>
              <RoomAccessQrManagementPage />
            </PermissionRoute>
          }
        />
      </Route>
    </>
  );
};

export default HotelRoutes;
